import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useIntegrations, useHasAccess, AccessList } from '~/Hooks';

import {
  IItemCreationSubsectionInputProps,
  ItemCreationSubsection,
  TextEditor,
} from '~/Components';
import { ExtendedEventGroup } from '../../ExtendedEventGroup.model';
import { SportCategoryTypeSelection, EventImageUpload } from '../customInputs';
import { ExtendedEvent } from '../../ExtendedEvent.model';
import { EditEventGroupButton } from '../EditEventGroupButton';
import { ItemCreationValidInput } from '~/Components/ItemCreation/ItemCreationSubsection/ItemCreationSubsectionInput.model';
import { ExternalPartyID } from '~/Models/DTO/externalParties.model';

export const EventDetailsSection = ({
  eventGroup,
  event,
}: {
  eventGroup: ExtendedEventGroup;
  event?: ExtendedEvent;
}) => {
  const { t } = useTranslation();
  const boundariesElement = useRef(null);
  const { hasIntegration } = useIntegrations();
  const { hasAccess } = useHasAccess();

  const isUSCIntegrationEnabled = useMemo(() => hasIntegration(ExternalPartyID.USC_INTEGRATION), [
    hasIntegration,
  ]);

  const shouldNotDisplayImageUpload =
    isUSCIntegrationEnabled || !hasAccess(AccessList.CAN_UPLOAD_EVENT_IMAGE);
  const hasAccessToTheSportCategoryTooltip = hasAccess(AccessList.SPORTS_CATEGORY_TOOLTIP);
  const hasAccessToTheMinimalDescriptionEditor = hasAccess(
    AccessList.MINIMAL_EVENT_GROUP_DESCRIPTION_EDITOR,
  );

  const DetailsSectionInputs: IItemCreationSubsectionInputProps<Partial<ExtendedEventGroup>>[] = [
    {
      customElement: <EventImageUpload disabled={!!event} />,
      hidden: shouldNotDisplayImageUpload,
    },
    {
      label: `${t('event.color')}*`,
      type: ItemCreationValidInput.COLOR,
      name: 'color',
      disabled: !!event,
    },
    {
      customElement: (
        <SportCategoryTypeSelection
          disabled={!!event}
          tooltip={
            hasAccessToTheSportCategoryTooltip && {
              title: t('partner.event.create.tooltip.sportCategory'),
              placement: 'bottom-start',
            }
          }
        />
      ),
    },
  ];

  const DescriptionSectionInputs: IItemCreationSubsectionInputProps<
    Partial<ExtendedEventGroup>
  >[] = [
    {
      customElement: (
        <TextEditor
          firstItem={!!event}
          title={t('event.description')}
          fieldName="description"
          tooltip={
            hasAccessToTheMinimalDescriptionEditor && {
              title: t('partner.event.create.tooltip.description'),
              placement: 'bottom-start',
            }
          }
          noToolbarSettings={hasAccessToTheMinimalDescriptionEditor}
          isLanguageSelectorAlwaysOn={hasAccessToTheMinimalDescriptionEditor}
          withoutTranslation
        />
      ),
      customElementFullSection: true,
    },
  ];

  const filteredInputs = useMemo(() => DetailsSectionInputs.filter((item) => !item.hidden), [
    DetailsSectionInputs,
  ]);

  return (
    <div ref={boundariesElement}>
      {!!event && (
        <>
          <ItemCreationSubsection
            title="EVENT_CREATE_DESCRIPTION_SECTION_1"
            isHeaderVisible={false}
            items={DescriptionSectionInputs}
          />
          <EditEventGroupButton eventGroup={eventGroup} eventType={event.type} />
        </>
      )}
      <ItemCreationSubsection
        title={t('app.title.generalInformation')}
        items={filteredInputs}
        tooltip={
          !shouldNotDisplayImageUpload && {
            title: t('event.imageHint'),
            boundariesElement: boundariesElement.current,
          }
        }
      />
      {!event && (
        <ItemCreationSubsection
          title="EVENT_CREATE_DESCRIPTION_SECTION_1"
          isHeaderVisible={false}
          items={DescriptionSectionInputs}
        />
      )}
    </div>
  );
};
